/* global axios */

const DEFAULT_API_VERSION = 'v1';

// --- Integrity sub-app base-path patch (Spike-02c / ADR-0007 Option B) ------
// Chatwoot's SPA assumes it is mounted at the origin root. When Integrity
// embeds the agent dashboard under `<origin>/support/*`, the REST base and the
// account-id parsed out of the URL must both become prefix-aware. The prefix is
// injected once, server-side, into `window.chatwootConfig.basePath` (see
// `app/views/layouts/vueapp.html.erb`). Empty string ('') => original root
// behaviour, so this is a no-op on a stock (root-mounted) deployment.
const subAppBasePath = () =>
  (typeof window !== 'undefined' &&
    window.chatwootConfig &&
    window.chatwootConfig.basePath) ||
  '';

class ApiClient {
  constructor(resource, options = {}) {
    this.apiVersion = `/api/${options.apiVersion || DEFAULT_API_VERSION}`;
    this.options = options;
    this.resource = resource;
  }

  get url() {
    return `${this.baseUrl()}/${this.resource}`;
  }

  // The window pathname with the injected sub-app base path stripped, so the
  // account-id is always parsed from the Chatwoot-relative path regardless of
  // the mount prefix. At root (basePath '') this returns the pathname unchanged.
  // eslint-disable-next-line class-methods-use-this
  get chatwootPathname() {
    const pathname = window.location.pathname;
    const basePath = subAppBasePath();
    if (basePath && pathname.startsWith(basePath)) {
      return pathname.slice(basePath.length) || '/';
    }
    return pathname;
  }

  // eslint-disable-next-line class-methods-use-this
  get accountIdFromRoute() {
    const pathname = this.chatwootPathname;
    const isInsideAccountScopedURLs = pathname.includes('/app/accounts');

    if (isInsideAccountScopedURLs) {
      return pathname.split('/')[3];
    }

    return '';
  }

  baseUrl() {
    // Prefix the REST base with the sub-app mount path so axios (which has no
    // global baseURL) targets `<basePath>/api/v1/...` — reachable through the
    // Integrity proxy — instead of the origin-root `/api/v1/...`.
    let url = `${subAppBasePath()}${this.apiVersion}`;

    if (this.options.enterprise) {
      url = `${subAppBasePath()}/enterprise${this.apiVersion}`;
    }

    if (this.options.accountScoped && this.accountIdFromRoute) {
      url = `${url}/accounts/${this.accountIdFromRoute}`;
    }

    return url;
  }

  get() {
    return axios.get(this.url);
  }

  show(id) {
    return axios.get(`${this.url}/${id}`);
  }

  create(data) {
    return axios.post(this.url, data);
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}`, data);
  }

  delete(id) {
    return axios.delete(`${this.url}/${id}`);
  }
}

export default ApiClient;
